import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiPackage, 
  FiMapPin, 
  FiClock, 
  FiTruck, 
  FiCheckCircle, 
  FiBox,
  FiActivity,
  FiNavigation,
  FiCalendar,
  FiUser,
  FiArrowLeft
} from 'react-icons/fi';
import './TrackingResults.css';

const TrackingResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [shipmentData, setShipmentData] = useState(null);

  useEffect(() => {
    // Get tracking data from navigation state
    if (location.state?.shipmentData) {
      setShipmentData(location.state.shipmentData);
    } else {
      // If no data, redirect back to home
      navigate('/');
    }
  }, [location.state, navigate]);

  // Get status color based on progress
  const getStatusColor = (progress) => {
    if (progress === 5) return '#10b981'; // Green for delivered
    if (progress >= 2) return '#0d9e84'; // Brand color for in transit
    return '#64748b'; // Gray for initial stages
  };

  // Get status icon based on progress
  const getStatusIcon = (progress) => {
    if (progress === 5) return FiCheckCircle;
    return FiTruck;
  };

  if (!shipmentData) {
    return (
      <div className="tracking-results page-transition">
        <div className="container">
          <div className="tracking-results-loading">
            <p>Loading tracking information...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tracking-results page-transition">
      <div className="container">
        {/* Back Button */}
        <motion.button
          className="tracking-results-back"
          onClick={() => navigate('/')}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <FiArrowLeft /> Back to Tracking
        </motion.button>

        {/* Shipment Result Card */}
        <motion.div
          className="track-result-card"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Shipment Header */}
          <div className="track-result-header">
            <div className="track-result-header-left">
              <div 
                className="track-result-icon-wrapper"
                style={{ background: `linear-gradient(135deg, ${getStatusColor(shipmentData.shipmentProgress)} 0%, #086050 100%)` }}
              >
                {React.createElement(getStatusIcon(shipmentData.shipmentProgress), { className: 'track-result-icon' })}
              </div>
              <div className="track-result-info">
                <h1 className="track-result-title">Shipment Found</h1>
                <div className="track-result-numbers">
                  <p className="track-result-number">
                    <FiPackage /> Airwaybill: <strong>{shipmentData.airWayBillNo}</strong>
                  </p>
                  {shipmentData.shipperReference && (
                    <p className="track-result-number">
                      <FiBox /> Reference: <strong>{shipmentData.shipperReference}</strong>
                    </p>
                  )}
                </div>
                <span 
                  className="track-status-badge"
                  style={{ 
                    background: getStatusColor(shipmentData.shipmentProgress),
                    color: 'white'
                  }}
                >
                  {shipmentData.status}
                </span>
              </div>
            </div>
            
            {/* Current Status Card - Right Side */}
            {shipmentData.trackingLogDetails && shipmentData.trackingLogDetails.length > 0 && (
              <motion.div
                className="track-result-status-card"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="track-result-status-header">
                  <div className="track-result-status-icon-wrapper">
                    <FiClock />
                  </div>
                  <span className="track-result-status-label">Current Status</span>
                </div>
                <div className="track-result-status-content">
                  {/* Show Remarks if available, otherwise show readable status */}
                  {shipmentData.trackingLogDetails[0].Remarks ? (
                    <>
                      <h3 className="track-result-status-title">
                        {shipmentData.trackingLogDetails[0].Remarks.length > 75
                          ? `${shipmentData.trackingLogDetails[0].Remarks.substring(0, 75)}...`
                          : shipmentData.trackingLogDetails[0].Remarks}
                      </h3>
                      {shipmentData.trackingLogDetails[0].Remarks.length > 75 && (
                        <p className="track-result-status-note">
                          <FiClock /> See tracking history below for complete details
                        </p>
                      )}
                    </>
                  ) : (
                    <h3 className="track-result-status-title">
                      {shipmentData.status}
                    </h3>
                  )}
                  <div className="track-result-status-details">
                    <div className="track-result-status-detail-item">
                      <FiCalendar />
                      <span>
                        {shipmentData.trackingLogDetails[0].ActivityDate || 'N/A'}
                      </span>
                    </div>
                    <div className="track-result-status-detail-item">
                      <FiClock />
                      <span>
                        {shipmentData.trackingLogDetails[0].ActivityTime || 'N/A'}
                      </span>
                    </div>
                    {shipmentData.trackingLogDetails[0].Location && (
                      <div className="track-result-status-detail-item">
                        <FiMapPin />
                        <span>{shipmentData.trackingLogDetails[0].Location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Shipment Details */}
          <div className="track-result-details">
            <div className="track-detail-row">
              <div className="track-detail-item">
                <div className="track-detail-icon">
                  <FiNavigation />
                </div>
                <div className="track-detail-content">
                  <span className="track-detail-label">Origin</span>
                  <span className="track-detail-value">{shipmentData.origin || 'N/A'}</span>
                </div>
              </div>

              <div className="track-detail-item">
                <div className="track-detail-icon">
                  <FiMapPin />
                </div>
                <div className="track-detail-content">
                  <span className="track-detail-label">Destination</span>
                  <span className="track-detail-value">{shipmentData.destination || 'N/A'}</span>
                </div>
              </div>
            </div>

            <div className="track-detail-row">
              <div className="track-detail-item">
                <div className="track-detail-icon">
                  <FiActivity />
                </div>
                <div className="track-detail-content">
                  <span className="track-detail-label">Weight</span>
                  <span className="track-detail-value">{shipmentData.weight || 'N/A'} kg</span>
                </div>
              </div>

              {shipmentData.forwardingNumber && (
                <div className="track-detail-item">
                  <div className="track-detail-icon">
                    <FiTruck />
                  </div>
                  <div className="track-detail-content">
                    <span className="track-detail-label">Forwarding Number</span>
                    <span className="track-detail-value">{shipmentData.forwardingNumber}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Progress Bar with Origin/Destination */}
          <div className="track-progress-section">
            <div className="track-progress-header">
              <h3 className="track-progress-title">
                <FiTruck /> Shipment Journey
              </h3>
              <span className="track-progress-percentage">
                {shipmentData.shipmentProgress === 5 ? '100%' : `${shipmentData.shipmentProgress * 20}%`}
              </span>
            </div>
            
            <div className="track-progress-container">
              {/* Origin Label */}
              <div className="track-progress-endpoint track-progress-origin">
                <motion.div
                  className="track-progress-endpoint-icon"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <FiNavigation />
                </motion.div>
                <motion.div
                  className="track-progress-endpoint-label"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                >
                  <span className="track-progress-endpoint-title">Origin</span>
                  <span className="track-progress-endpoint-value">
                    {shipmentData.origin || 'N/A'}
                  </span>
                </motion.div>
              </div>

              {/* Progress Bar */}
              <div className="track-progress-bar-wrapper">
                <div className="track-progress-bar">
                  {/* Animated fill */}
                  <motion.div
                    className="track-progress-fill"
                    initial={{ width: 0 }}
                    animate={{ width: `${shipmentData.shipmentProgress === 5 ? 100 : shipmentData.shipmentProgress * 20}%` }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                    style={{ 
                      background: `linear-gradient(90deg, ${getStatusColor(shipmentData.shipmentProgress)} 0%, ${getStatusColor(shipmentData.shipmentProgress)}dd 100%)`
                    }}
                  >
                    {/* Animated shimmer effect */}
                    <motion.div
                      className="track-progress-shimmer"
                      animate={{
                        x: ['-100%', '200%'],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'linear',
                      }}
                    />
                  </motion.div>

                  {/* Moving truck icon */}
                  <motion.div
                    className="track-progress-truck"
                    initial={{ left: '0%' }}
                    animate={{ 
                      left: `${shipmentData.shipmentProgress === 5 ? 100 : shipmentData.shipmentProgress * 20}%`,
                      x: '-50%'
                    }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                  >
                    <motion.div
                      className="track-progress-truck-icon"
                      animate={{
                        y: [0, -8, 0],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    >
                      {React.createElement(getStatusIcon(shipmentData.shipmentProgress), { 
                        size: 24,
                        style: { color: getStatusColor(shipmentData.shipmentProgress) }
                      })}
                    </motion.div>
                    <motion.div
                      className="track-progress-truck-pulse"
                      animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.5, 0, 0.5],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                  </motion.div>

                  {/* Milestone markers */}
                  {[1, 2, 3, 4, 5].map((milestone) => {
                    const isCompleted = shipmentData.shipmentProgress >= milestone;
                    const progressPercent = milestone * 20;
                    return (
                      <motion.div
                        key={milestone}
                        className={`track-progress-milestone ${isCompleted ? 'completed' : ''}`}
                        style={{ left: `${progressPercent}%` }}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.3, delay: 0.5 + (milestone * 0.1) }}
                      >
                        <div className="track-progress-milestone-dot" />
                        {milestone === 5 && (
                          <motion.div
                            className="track-progress-milestone-check"
                            initial={{ scale: 0, rotate: -180 }}
                            animate={isCompleted ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                            transition={{ duration: 0.5, delay: 1.5 }}
                          >
                            <FiCheckCircle />
                          </motion.div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Destination Label */}
              <div className="track-progress-endpoint track-progress-destination">
                <motion.div
                  className="track-progress-endpoint-icon"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                >
                  <FiMapPin />
                </motion.div>
                <motion.div
                  className="track-progress-endpoint-label"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                >
                  <span className="track-progress-endpoint-title">Destination</span>
                  <span className="track-progress-endpoint-value">
                    {shipmentData.destination || 'N/A'}
                  </span>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Compact Tracking History */}
          {shipmentData.trackingLogDetails && shipmentData.trackingLogDetails.length > 0 && (
            <div className="track-timeline-section">
              <h4 className="track-timeline-title">
                <FiClock /> Tracking History
              </h4>
              <div className="track-history-table">
                <div className="track-history-header">
                  <div className="track-history-col-number">#</div>
                  <div className="track-history-col-date">Date & Time</div>
                  <div className="track-history-col-location">Location</div>
                  <div className="track-history-col-remarks">Remarks</div>
                </div>
                <div className="track-history-body">
                  {shipmentData.trackingLogDetails.map((log, index) => {
                    const totalItems = shipmentData.trackingLogDetails.length;
                    const serialNumber = totalItems - index; // Reverse order: most recent = highest number (3, 2, 1)
                    const isActive = index === 0;
                    
                    return (
                      <motion.div
                        key={index}
                        className={`track-history-row ${isActive ? 'track-history-row-active' : ''}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <div className="track-history-col-number">
                          <div className="track-history-number-badge">{serialNumber}</div>
                        </div>
                        <div className="track-history-col-date">
                          <div className="track-history-date-part">
                            <FiCalendar /> {log.ActivityDate || 'N/A'}
                          </div>
                          <div className="track-history-time-part">
                            <FiClock /> {log.ActivityTime || 'N/A'}
                          </div>
                        </div>
                        <div className="track-history-col-location">
                          <div className="track-history-location">
                            <FiMapPin /> {log.Location || 'N/A'}
                          </div>
                        </div>
                        <div className="track-history-col-remarks">
                          <div className="track-history-remarks">{log.Remarks || 'N/A'}</div>
                          {log.DeliveredTo && (
                            <div className="track-history-delivered">
                              <FiUser /> Delivered to: <strong>{log.DeliveredTo}</strong>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default TrackingResults;

